/* eslint-disable no-open-modules-imports */

import { ReplyNotificationsFeature } from 'feature/comments/reply-notifs';
import { UpvoteFeature } from 'feature/comments/upvote';
import type { FeatureGroup } from 'feature/feature-helpers';
import { CHOOSEONE, COMPOSITE, SECTION } from 'feature/feature-helpers';
import { OpenLinksInNewTabFeature } from 'feature/general/open-links-in-new-tab';
import { SatisfactionSurveyFeature, UsabilitySurveyFeature } from 'feature/general/survey';
import {
    ModerateWithGPTFeature,
    ModerateWithJigsawFeature,
    ModerationHelpFeature,
    PreModeratorFeature,
} from 'feature/moderation/ai-premoderator';
import { HideRejectedFeature } from 'feature/moderation/hide-rejected';
import { HumanReviewsEverythingFeature } from 'feature/moderation/human-reviews-everything';
import { ReportCommentFeature } from 'feature/moderation/report-comment';
import { CloseConversationFeature } from 'feature/question/close-conversation';
import { CommunityGuidelinesFeature, CommunityGuidelinesLeftFeature } from 'feature/question/community-guidelines';
import { ContextEntryPointsFeature } from 'feature/question/context-entry-points';
import { PinCommentFeature } from 'feature/question/pin-comment';
import { RecordEntryUrlFeature } from 'feature/question/record-entry-url';
import { TopicPhotoTeaserForTopicFeature } from 'feature/teaser/topic-photo-teaser';
import { CommentsSortFeature } from 'open-modules/cbcrc/feature/comments/comments-sort';
import { DownvoteFeature } from 'open-modules/cbcrc/feature/comments/downvote';
import { TitledWriterFeature } from 'open-modules/cbcrc/feature/general/titled-writer';
import { AskMeAnythingHeaderFeature } from 'open-modules/cbcrc/feature/topic/ask-me-anything-header';
import { EditorialReadingListFeature } from 'open-modules/cbcrc/feature/topic/editorial-reading-list';
import { TopicLiveStatusRightFeature } from 'open-modules/cbcrc/feature/topic/live-status';
import { PinAnswerFeature } from 'open-modules/cbcrc/feature/topic/pin-answer';
import { TopicRelatedConversationsFeature } from 'open-modules/cbcrc/feature/topic/related-conversations';
import {
    AskMeAnythingConfigFeature,
    BaseTopicConfigFeature,
    TopicOfTheMomentConfigFeature,
} from 'open-modules/cbcrc/feature/topic/TopicOfTheMomentConfigFeature';
import { TopicParticipantsFeature } from 'open-modules/cbcrc/feature/topic/TopicParticipants';
import {
    NextTopicPollFeature,
    TopicPollBottomPositionFeature,
    TopicPollFeature,
    TopicPollInContentPositionFeature,
    TopicPollTopPositionFeature,
} from 'open-modules/cbcrc/feature/topic/topic-poll';
import {
    TopicOfTheMomentCallToActionFeature,
    TopicPopularQuestionsFeature,
    TopicUserQuestionCallToActionFeature,
    TopicUserQuestionsFeature,
} from 'open-modules/cbcrc/feature/topic/user-questions';
import { AutoApproveFeature } from 'open-modules/zdf/feature/premoderation/AutoApproveFeature';
import { BlocklistFeature } from 'open-modules/zdf/feature/premoderation/blocklist';
import { PushNotificationFeaturesForThreads } from 'open-modules/zdf/feature/pushNotifications/ThreadPushNotification';
import { DeleteCommentsInModDashboardFeature } from 'open-modules/zdf/feature/question/delete-commentsin-mod-dashboard';
import { EnableModTaskTreeViewFeature } from 'open-modules/zdf/feature/question/enable-mod-task-tree-view';
import { EnableModDashboardThreadDetailsFeature } from 'open-modules/zdf/feature/question/enable-thread-mod-dashboard-view';
import { t } from 'util/stdtypes';

import { QuestionCardsFeature } from './question-cards';
import { ReadingListFeature } from './reading-list';
import { TopicConfigFeature } from './topic-config';

export const topicFeatures: FeatureGroup[] = [
    SECTION(t('Topic template'), [
        COMPOSITE(BaseTopicConfigFeature, [TopicUserQuestionCallToActionFeature]),
        COMPOSITE(TopicOfTheMomentConfigFeature, [
            TopicLiveStatusRightFeature,
            TopicParticipantsFeature,
            TopicOfTheMomentCallToActionFeature,
            CommunityGuidelinesLeftFeature,
        ]),
        COMPOSITE(AskMeAnythingConfigFeature, [AskMeAnythingHeaderFeature, CommunityGuidelinesLeftFeature]),
    ]),
    SECTION(t('Content'), [
        TopicUserQuestionsFeature, // DEV feature enabled by default
        TopicPopularQuestionsFeature, // DEV feature enabled by default
        COMPOSITE(TopicPollFeature, [
            CHOOSEONE(t('Position'), [TopicPollTopPositionFeature, TopicPollInContentPositionFeature, TopicPollBottomPositionFeature]),
        ]),
        TopicRelatedConversationsFeature,
        EditorialReadingListFeature,
        NextTopicPollFeature,
        QuestionCardsFeature, // DEV feature
        UpvoteFeature, // DEV feature enabled by default
        DownvoteFeature,
    ]),
    SECTION(t('Moderation'), [
        HideRejectedFeature, // DEV feature enabled by default
        COMPOSITE(PreModeratorFeature, [ModerateWithGPTFeature, ModerateWithJigsawFeature, BlocklistFeature]),
        AutoApproveFeature,
        ModerationHelpFeature, // DEV feature enabled by default
        ReportCommentFeature,
        EnableModDashboardThreadDetailsFeature,
        EnableModTaskTreeViewFeature,
        DeleteCommentsInModDashboardFeature,
    ]),
    SECTION(t('General'), [
        CommunityGuidelinesFeature, // DEV feature enabled by default
        ContextEntryPointsFeature,
        ReadingListFeature,
        OpenLinksInNewTabFeature,
        PinCommentFeature,
        ReplyNotificationsFeature, // DEV feature enabled by default
        RecordEntryUrlFeature, // DEV feature enabled by default
        TopicPhotoTeaserForTopicFeature, // DEV feature
        TopicConfigFeature, // DEV feature disabled by default
        TitledWriterFeature, // DEV feature enabled by default
        PinAnswerFeature, // DEV feature enabled by default
        CommentsSortFeature, // DEV feature enabled by default
        SatisfactionSurveyFeature, // DEV feature enabled by default
        UsabilitySurveyFeature, // DEV feature enabled by default
        EnableModDashboardThreadDetailsFeature, // Dev feature, enables detail view within mod dashboard
    ]),
    SECTION(t('Fallbacks'), [
        HumanReviewsEverythingFeature,
        CloseConversationFeature, // this needs to be supported in Topic, right now it doesnt do anything
    ]),
    PushNotificationFeaturesForThreads,
];

export const topicDefaultConfig = {
    editorialtopic: true,
    topictype: true,
    poll: true,
    upvote: true,
    downvote: false,
    commentssort: true,
    premoderator: false,
    record_entry_url: true,
    context_entry_points: true,
    community_guidelines: true,
    pin_comment: true,
    satisfaction_survey: true,
    usability_survey: true,

    // All CBC modules on by default for now
    amaheader: true,
    basetopicconfig: true,
    topicuserctaquestions: true,
    topicpopularquestions: true,
    topicuserquestions: true,
    topicrelatedcontent: true,
    topicpoll: true,
    nexttopicpoll: true,
    topicfollow: true,
    topiclivestatusright: true,
    topicparticipants: true,
    titledWriter: true,
    pin_answer: true,
    tomuserctaquestions: true,

    topicphototeaser_topic: true,
    replynotifications: true,

    // Moderation
    topicaimoderationtag: true,
    hide_rejected: true,
    aimoderation: true,
    jigsawmoderation: true,
    premoderation: true,
    report_comment: true,
    moderationhelp: true,
    autoapprovecomments: true,
    deletecommentsinmoddashboard: true,
};
